import { ReadonlyRecord } from './ReadonlyRecord';
import { notNullish } from './utilities';

/** Returns a description of the input in use if that input needs special keypress handling, otherwise null. */
function getSpecialInput(element: HTMLElement) {
  const tag = element.tagName.toLowerCase();

  if (tag === 'select' || tag === 'textarea') return tag;
  if (tag === 'input') {
    // checkbox & radio inputs aren't text inputs. Exclude them so hotkeys won't be blocked when one is focused.
    const type = (element as HTMLInputElement).type;
    if (type === 'checkbox' || type === 'radio') return null;

    return tag;
  }

  return null;
}

export function isInput(element: HTMLElement): boolean {
  return getSpecialInput(element) != null;
}

export function isInputFocused() {
  const activeElement = document.activeElement;
  return activeElement instanceof HTMLElement && isInput(activeElement);
}

export function isMac() {
  return window.navigator.platform.startsWith('Mac');
}

export type ModifierKey = 'ctrlKey' | 'altKey' | 'shiftKey';

export type ModifierKeys = Partial<Record<ModifierKey, boolean>>;

type ModifierKeyEvent = Record<ModifierKey | 'metaKey', boolean>;

export function getModifierKeys(event: ModifierKeyEvent): Record<ModifierKey, boolean> {
  const ctrlKey = isMac() ? event.metaKey : event.ctrlKey;
  const { altKey, shiftKey } = event;

  return { ctrlKey, altKey, shiftKey };
}

export interface KeyCombination {
  readonly modifierKeys: ModifierKeys;
  readonly keyCode: string;
  readonly allowMacCtrlKey?: boolean;
}

function ignoreKeyEventDueToInputFocus(event: KeyboardEvent): boolean {
  const inputType = getSpecialInput(event.target as HTMLElement);
  if (inputType == null) return false;

  // Simple typing. We check the event directly here to ensure each key is covered, because getModifierKeys sometimes
  //   lies for cross-platform compatibility.
  if (!event.ctrlKey && !event.altKey && !event.metaKey) return true;

  // Navigation in various inputs
  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Backspace'].includes(event.code))
    return true;

  const modifiers = getModifierKeys(event);

  if (inputType === 'input' || inputType === 'textarea') {
    // Text selection/clipboard commands
    if (
      modifiers.ctrlKey &&
      !modifiers.altKey &&
      !modifiers.shiftKey &&
      ['KeyA', 'KeyC', 'KeyV', 'KeyX'].includes(event.code)
    )
      return true;

    // Text navigation commands/insert/delete
    if (['Home', 'End', 'PageUp', 'PageDown', 'Insert', 'Delete'].includes(event.code)) return true;
  }

  return false;
}

export type Keybindings<THotkeyActionType extends string> = ReadonlyRecord<
  THotkeyActionType,
  readonly KeyCombination[]
>;

export type HotkeyActionResolver<THotkeyActionType extends string, TAction> = (
  type: THotkeyActionType,
  keyup: boolean,
) => { type: THotkeyActionType; resolution: TAction } | null;

function eventMatchesKeyCombination(event: KeyboardEvent, keyCombination: KeyCombination): boolean {
  const modifiers = getModifierKeys(event);

  let { ctrlKey } = modifiers;
  const { altKey, shiftKey } = modifiers;

  if (isMac() && keyCombination.allowMacCtrlKey) {
    ctrlKey = ctrlKey || event.ctrlKey;
  }

  return (
    event.code === keyCombination.keyCode &&
    [
      [ctrlKey, keyCombination.modifierKeys.ctrlKey],
      [altKey, keyCombination.modifierKeys.altKey],
      [shiftKey, keyCombination.modifierKeys.shiftKey],
    ].every(([eventModifier, requiredModifier]) => eventModifier === (requiredModifier ?? false))
  );
}

export function resolveHotkeyAction<TAction, THotkeyActionType extends string>(
  event: KeyboardEvent,
  keybindings: Keybindings<THotkeyActionType>,
  hotkeyActionResolver: HotkeyActionResolver<THotkeyActionType, TAction>,
) {
  if (ignoreKeyEventDueToInputFocus(event)) return { result: 'noAction' } as const;

  const possibleActions = Object.entries<readonly KeyCombination[]>(keybindings)
    .map(([actionType, keyCombinations]) =>
      keyCombinations.some((keyCombination) => eventMatchesKeyCombination(event, keyCombination))
        ? (actionType as THotkeyActionType)
        : null,
    )
    .filter(notNullish)
    .map((actionType) => hotkeyActionResolver(actionType, event.type === 'keyup'))
    .filter(notNullish);

  switch (possibleActions.length) {
    case 0:
      return { result: 'noAction' } as const;
    case 1:
      return { result: 'singleAction', action: possibleActions[0] } as const;
    default:
      return { result: 'multipleActions' } as const;
  }
}
